import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, SelectOption } from '@fcom/common/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';
import { FinnairItineraryItemFlight, FinnairServiceItem } from '@fcom/dapi/api/models';
import { IconButtonSize, IconButtonTheme } from '@fcom/ui-components';
import { getIconForServiceCategory } from '@fcom/dapi/utils';

import { getSeatMapFlight, servicesForFlightAndTravelerId } from '../../../utils';
import { SeatMapTraveler } from '../../../../ancillaries/interfaces';

@Component({
  selector: 'fin-seat-passengers',
  templateUrl: './seat-passengers.component.html',
  styleUrls: ['./seat-passengers.component.scss'],
  standalone: false,
})
export class SeatPassengersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() flight: FinnairItineraryItemFlight;
  @Input() includedSeats: FinnairServiceItem | null;

  @Output() selectPax: EventEmitter<string> = new EventEmitter();
  @Output() clearSeatSelection: EventEmitter<{ passengerId: string; seatNumber: string; exitRow: boolean }> =
    new EventEmitter();

  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  passengerForm: FormGroup;
  options$: Observable<SelectOption[]>;
  subscriptions: Subscription = new Subscription();
  activeTravelerIndex$ = new BehaviorSubject(1);
  passengersWithBundleIcons$: Observable<Array<SeatMapTraveler & { bundleIcon?: SvgLibraryIcon }>>;

  constructor(
    private formBuilder: FormBuilder,
    private languageService: LanguageService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.passengerForm = this.formBuilder.group({
      activeTravelerId: new FormControl(null),
    });

    this.subscriptions.add(
      this.passengerForm
        .get('activeTravelerId')
        .valueChanges.pipe(withLatestFrom(this.passengers$))
        .subscribe(([activeTravelerId, passengers]) => {
          this.selectPax.emit(activeTravelerId);
          const activeTraveler = this.getActiveTraveler(passengers);

          if (this.activeTravelerIndex$.getValue() !== activeTraveler.index + 1) {
            this.activeTravelerIndex$.next(activeTraveler.index + 1);
          }
        })
    );

    this.passengersWithBundleIcons$ = this.passengers$.pipe(
      map((passengers) =>
        passengers.map((passenger) => ({
          ...passenger,
          bundleIcon:
            passenger.seatNumber && passenger.bundleActive && passenger.bundleCategory
              ? getIconForServiceCategory(passenger.bundleCategory)
              : undefined,
        }))
      )
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.passengers || !this.passengerForm) {
      return;
    }

    const idControl = this.passengerForm.get('activeTravelerId') as FormControl;
    const activeTraveler = this.getActiveTraveler(changes.passengers.currentValue);

    if (idControl.value !== activeTraveler.id) {
      idControl.setValue(activeTraveler.id);
    }

    if (this.activeTravelerIndex$.getValue() !== activeTraveler.index + 1) {
      this.activeTravelerIndex$.next(activeTraveler.index + 1);
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  getActiveTraveler(passengers: SeatMapTraveler[]): {
    id: string;
    index: number;
  } {
    return {
      id: passengers.find((traveler) => traveler.active).id,
      index: passengers.findIndex((traveler) => traveler.active),
    };
  }

  createForm(): void {
    this.options$ = combineLatest([this.languageService.translate('seatMap.and'), this.passengers$]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(([_withInfantLabel, passengers]) => {
        const activeTraveler = this.getActiveTraveler(passengers);

        this.passengerForm.get('activeTravelerId').setValue(activeTraveler.id);
      }),
      switchMap(([withInfantLabel, passengers]) => {
        const bundleTranslations$ = passengers.some((p) => p.bundleCategory)
          ? combineLatest(
              [...new Set(passengers.filter((p) => p.bundleCategory).map((p) => p.bundleCategory))].map((category) =>
                this.languageService
                  .translate(`services.${category}.title`)
                  .pipe(map((translation) => ({ category, translation })))
              )
            )
          : of([]);

        return bundleTranslations$.pipe(
          map((bundleTranslations) => {
            return passengers.map((passenger) => {
              const paxName = `${passenger.traveler.firstName} ${passenger.traveler.lastName}`;
              const paxNameWithInfant = `${paxName} ${withInfantLabel} ${passenger.withInfantFullName}`;
              const seatNumber = passenger.seatNumber ? `, ${passenger.seatNumber}` : '';
              const bundleTitle = passenger.bundleCategory
                ? ` + ${bundleTranslations.find((bt) => bt.category === passenger.bundleCategory)?.translation || ''}`
                : '';

              return {
                name: `${passenger.withInfant ? paxNameWithInfant : paxName}${seatNumber}${bundleTitle}`,
                value: passenger.id,
              };
            });
          })
        );
      })
    );
  }

  handleClick(passengerId: string, index: number): void {
    this.selectPax.emit(passengerId);

    this.gtmService.seatMapEvent(`pax-select-${index + 1}`, ElementTypes.BUTTON, '', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  clearSelection(passengerId: string): void {
    const alreadyIncludedSeat = servicesForFlightAndTravelerId(this.includedSeats, this.flight.id, passengerId)[0];

    this.clearSeatSelection.emit({ passengerId, seatNumber: alreadyIncludedSeat?.seatNumber ?? null, exitRow: false });
  }
}
